import React, { useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useNavigate } from 'react-router-dom';
import { RfiData, RfiStatus, VendorResponse } from 'types/RfiTypes';

interface ISingleMyActiveRfiProps {
  rfi?: RfiData | null;
}

const SingleMyActiveRfi = (props: ISingleMyActiveRfiProps) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(localizedFormat);
  dayjs.extend(advancedFormat);

  // Hooks
  const navigate = useNavigate();

  // State
  const [vendorQuestions, setVendorQuestions] = React.useState(0);
  const [vendorResponses, setVendorResponses] = useState<VendorResponse[]>([]);
  const [pastResponseDueDate, setPastResponseDueDate] = useState(false);
  const [pastQACloseDate, setPastQACloseDate] = useState(false);
  const warningDays = 10;

  // Functions
  function convertDate(responseDueDate: string): string {
    if (responseDueDate != null && responseDueDate != undefined) {
      const dateString = dayjs
        .utc(responseDueDate)
        .format("YYYY-MM-DDTHH:mm:ss");
      const date = dayjs.tz(
        dateString,
        props.rfi?.timeZone ?? "America/New_York"
      );
      const closeDateString = date.format("lll z");
      return closeDateString;
    }
    return responseDueDate;
  }

  const WarningAlert = () => {
    //Incoming Dates from RFI
    let qaCloseDate = dayjs(props.rfi?.qaCloseDate);
    let responseDueDate = dayjs(props.rfi?.responsesDueDate).utcOffset(
      props.rfi?.timeZone ?? "America/New_York"
    );

    //Local Time Variables
    let now = dayjs(
      dayjs().utcOffset(props.rfi?.timeZone ?? "America/New_York")
    );
    let end_of_today = dayjs(now).hour(23).minute(59).second(59);
    let end_of_one_day = dayjs(end_of_today).add(1, "day");
    let end_warning_period = now.add(warningDays, "day");

    /******Response Due Date States********/
    let hasResponseDueDatePassed = responseDueDate.isBefore(now);
    let isResponsesDueToday = responseDueDate.isBefore(end_of_today);
    let areResponsesDueTomorrow = responseDueDate.isBefore(end_of_one_day);
    let areResponsesDueInWarningPeriod =
      responseDueDate.isBefore(end_warning_period);
    let daysUntilResponseDueDate = responseDueDate.diff(now, "day") + 1;

    /******QA Close Due Date States********/
    let hasQaCloseDatePassed = qaCloseDate.isBefore(now);
    let isQaCloseDateToday = qaCloseDate.isBefore(end_of_today);
    let isQaCloseDateTomorrow = qaCloseDate.isBefore(end_of_one_day);
    let isQaCloseDateInWarningPeriod = qaCloseDate.isBefore(end_warning_period);
    let daysUntilQaCloseDate = qaCloseDate.diff(now, "day") + 1;

    // Response Due Date Alert
    if (
      !hasResponseDueDatePassed &&
      areResponsesDueInWarningPeriod &&
      responseDueDate.isBefore(qaCloseDate)
    ) {
      if (isResponsesDueToday) {
        return `Responses are due today.`;
      } else if (!isResponsesDueToday && areResponsesDueTomorrow) {
        return `Responses due in 1 day.`;
      } else {
        return `Responses due in ${daysUntilResponseDueDate} days.`;
      }
    } else if (
      !hasQaCloseDatePassed &&
      isQaCloseDateInWarningPeriod &&
      qaCloseDate.isBefore(responseDueDate)
    ) {
      if (isQaCloseDateToday) {
        return `Q&A period closes today.`;
      } else if (!isQaCloseDateToday && isQaCloseDateTomorrow) {
        return `Q&A period closes in 1 day.`;
      } else {
        return `Q&A periods closing in ${daysUntilQaCloseDate} days.`;
      }
    }
    return "";
  };

  /****UseEffects*****/
  //Setting State for Title/Dates Passed
  useEffect(() => {
    if (props.rfi?.responsesDueDate) {
      let responseDueDate = dayjs(props.rfi?.responsesDueDate);
      let now = dayjs(
        dayjs().tz(props.rfi?.timeZone ?? "America/New_York"),
        "YYYY-MM-DD"
      );
      setPastResponseDueDate(responseDueDate.isBefore(now));
    }

    if (props.rfi?.qaCloseDate) {
      let qaCloseDate = dayjs(props.rfi?.qaCloseDate);
      let now = dayjs(
        dayjs().tz(props.rfi?.timeZone ?? "America/New_York"),
        "YYYY-MM-DD"
      );
      setPastQACloseDate(qaCloseDate.isBefore(now));
    }
  }, [props.rfi?.title, props.rfi]);

  //Getting Vendor Responses
  useEffect(() => {
    if (props.rfi) {
      axios
        .get(`/api/v1/rfi/${props.rfi.rfiId}/vendorresponses`)
        .then((res) => {
          setVendorResponses(res.data);
        })
        .catch((error: Error | AxiosError) => {
          console.log(error);
        });
    }
  }, [props.rfi?.rfiId]);

  //Getting unanswered vendor questions
  useEffect(() => {
    axios
      .get(`/api/v1/rfi/${props.rfi?.rfiId}/VendorQuestions`, {
        params: { answerStatus: "Unanswered" },
      })
      .then((res) => {
        if (res.data) {
          setVendorQuestions(res.data.length);
        }
      })
      .catch((error) => {
        setVendorQuestions(0);
      });
  }, [props.rfi?.rfiId]);

  // Render
  return (
    <div
      tabIndex={0}
      className="card mb-3 my-active-card k-mr-2"
      key={props.rfi?.rfiId}
      onClick={() => navigate(`/edit/${props.rfi?.rfiId}`)}
      onKeyDown={(event: any) => {
        if (event.keyCode === 13) {
          navigate(`/edit/${props.rfi?.rfiId}`);
        }
      }}
    >
      <div
        style={{ backgroundColor: "#0078d4", color: "white" }}
        className="my-rfi-card-header h6 rounded-0 font-weight-bolder pt-1 pr-3 pb-1 k-pl-2 mb-0"
      >
        {props.rfi?.title.length && props.rfi?.title?.length <= 100 && (
          <div id="rfiName" aria-label={props.rfi?.title}>
            {props.rfi?.title}
          </div>
        )}

        {props.rfi?.title?.length && props.rfi?.title?.length > 100 && (
          <div id="rfiName" aria-label={props.rfi?.title}>
            {`${props.rfi?.title.substring(0, 100)}…`}{" "}
          </div>
        )}
      </div>

      <div className="card-body">
        {/* <div className="h6">{props.rfi?.title}</div> */}
        <ul className="rfiDetail">
          <li>
            <span>RFI:</span>
            <span style={{ textAlign: "center" }}>
              {props.rfi?.rfiNumber} /{" "}
              {props.rfi?.versionNumber
                ? props.rfi?.versionNumber.toString().padStart(3, "0")
                : "Base"}
            </span>
          </li>

          <li>
            <span>Status:</span>
            {props.rfi?.status ? (
              <span style={{ textAlign: "center" }}>{props.rfi?.status}</span>
            ) : (
              <span style={{ textAlign: "center" }}>N/A</span>
            )}
          </li>

          <li>
            <span>Responses Due:</span>
            {props.rfi?.responsesDueDate ? (
              <span style={{ textAlign: "center" }}>
                {convertDate(props.rfi?.responsesDueDate)}
              </span>
            ) : (
              <span style={{ textAlign: "center" }}>N/A</span>
            )}
          </li>

          <li>
            <span>Q&amp;A Closes:</span>
            {props.rfi?.qaCloseDate ? (
              <span style={{ textAlign: "center" }}>
                {convertDate(props.rfi?.qaCloseDate)}
              </span>
            ) : (
              <span style={{ textAlign: "center" }}>N/A</span>
            )}
          </li>
        </ul>

        {
          props.rfi?.status !== RfiStatus.POSTED ||
            (props.rfi?.status === RfiStatus.POSTED &&
              (!props.rfi.qaCloseDate || !props.rfi.responsesDueDate))
          //display nothing
        }

        {((props.rfi?.status === RfiStatus.POSTED &&
          props.rfi.qaCloseDate &&
          !props.rfi.responsesDueDate) ||
          (props.rfi?.status === RfiStatus.POSTED &&
            props.rfi.qaCloseDate &&
            props.rfi.responsesDueDate &&
            pastResponseDueDate === false)) && (
          <div
            className="alert alert-secondary  rounded-0 font-weight-bolder pt-1 pr-3 pb-1 pl-3 mb-0"
            style={{ textAlign: "center", fontWeight: "600" }}
          >
            Unanswered Questions: {vendorQuestions}
          </div>
        )}

        {((props.rfi?.status === RfiStatus.POSTED &&
          !props.rfi.qaCloseDate &&
          props.rfi.responsesDueDate &&
          vendorResponses) ||
          (props.rfi?.status === RfiStatus.POSTED &&
            props.rfi.qaCloseDate &&
            pastResponseDueDate === true &&
            vendorResponses)) && (
          <div
            className="alert alert-secondary  rounded-0 font-weight-bolder pt-1 pr-3 pb-1 pl-3 mb-0"
            style={{ textAlign: "center", fontWeight: "600" }}
          >
            Responses Received : {vendorResponses.length}
          </div>
        )}

        {WarningAlert() && (
          <div
            style={{ textAlign: "center", fontWeight: "600" }}
            className="alert alert-primary rounded-0 font-weight-bolder pt-1 pr-3 pb-1 pl-3 mb-0"
            role="alert"
          >
            {WarningAlert()}
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleMyActiveRfi;
